import { DBConfig } from "DBConfig";
import { initDB, useIndexedDB } from "react-indexed-db-hook";
import { useCallback, useMemo, useRef } from "react";

// Snackbar utility (assume a global or context-based snackbar, adapt as needed)
let showSnackbar: ((msg: string) => void) | undefined;
try {
	if (window && typeof (window as any).showSnackbar === "function") {
		showSnackbar = (window as any).showSnackbar;
	}
} catch {}

const MISSING_STORE_MESSAGE = "objectStore does not exists:";
const NOT_FOUND_MESSAGE = "One of the specified object stores was not found";
const READONLY_NAME_MESSAGE = "Cannot assign to read only property 'name'";
const DB_VERSION_CACHE_KEY = "flagbag_indexeddb_version";

let hasInitializedDb = false;

const cacheDbVersion = (version: number) => {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(DB_VERSION_CACHE_KEY, String(version));
};

export const getIndexedDbInitConfig = () => ({
	...DBConfig,
});

const getStoreMeta = (storeName: string) =>
	DBConfig.objectStoresMeta.find((meta) => meta.store === storeName);

const getMissingStoreNameFromError = (error: unknown): string | null => {
	const message = error instanceof Error ? error.message : String(error ?? "");
	if (!message.includes(MISSING_STORE_MESSAGE)) {
		return null;
	}
	return message.split(MISSING_STORE_MESSAGE)[1]?.trim() || null;
};

const openDb = (name: string, version?: number): Promise<IDBDatabase> =>
	new Promise((resolve, reject) => {
		const request = version !== undefined ? window.indexedDB.open(name, version) : window.indexedDB.open(name);
		request.onsuccess = () => {
			const db = request.result;
			db.onversionchange = () => db.close();
			resolve(db);
		};
		request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
	});

const deleteDb = (name: string): Promise<void> =>
	new Promise((resolve, reject) => {
		const request = window.indexedDB.deleteDatabase(name);
		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error || new Error(`Failed to delete DB: ${name}`));
		request.onblocked = () => {
			console.warn(`[IndexedDB] Delete blocked: ${name}, proceeding anyway.`);
			resolve();
		};
	});

const initializeWithConfig = (): boolean => {
	try {
		const runtimeConfig = getIndexedDbInitConfig();
		initDB(runtimeConfig);
		cacheDbVersion(runtimeConfig.version);
		hasInitializedDb = true;
		return true;
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : String(error ?? "");
		if (errorMessage.includes(READONLY_NAME_MESSAGE)) {
			hasInitializedDb = true;
			return true;
		}
		console.error("[IndexedDB] Failed to initialize DB.", error);
		return false;
	}
};

export const enforceIndexedDbVersionAndInit = async (): Promise<boolean> => {
	if (typeof window === "undefined" || !window.indexedDB) {
		return false;
	}

	try {
		const db = await openDb(DBConfig.name);
		const currentVersion = db.version;
		db.close();

		if (currentVersion !== DBConfig.version) {
			console.warn(`[IndexedDB] Version mismatch (existing: ${currentVersion}, expected: ${DBConfig.version}), clearing DB.`);
			await deleteDb(DBConfig.name);
			hasInitializedDb = false;
		}
	} catch (error) {
		const msg = error instanceof Error ? error.message : String(error ?? "");
		const isVersionConflict = msg.toLowerCase().includes("versionerror") || msg.includes("less than the existing version");
		if (isVersionConflict) {
			console.warn("[IndexedDB] Version conflict detected, clearing DB.", error);
			try {
				await deleteDb(DBConfig.name);
				hasInitializedDb = false;
			} catch (deleteError) {
				console.error("[IndexedDB] Failed to clear DB on version conflict.", deleteError);
				return false;
			}
		} else {
			console.warn("[IndexedDB] Version check failed, continue with init.", error);
		}
	}

	return initializeWithConfig();
};

const createObjectStoreByUpgrade = async (storeName: string): Promise<boolean> => {
	if (typeof window === "undefined" || !window.indexedDB) {
		return false;
	}
	const storeMeta = getStoreMeta(storeName);
	if (!storeMeta) {
		console.error(`[IndexedDB] Missing DBConfig schema for store: ${storeName}`);
		return false;
	}

	let db: IDBDatabase | null = null;
	try {
		db = await openDb(DBConfig.name);
		if (db.objectStoreNames.contains(storeName)) {
			db.close();
			return true;
		}
		const nextVersion = (db.version || 1) + 1;
		db.close();

		await new Promise<void>((resolve, reject) => {
			const upgradeRequest = window.indexedDB.open(DBConfig.name, nextVersion);
			upgradeRequest.onupgradeneeded = () => {
				const upgradeDb = upgradeRequest.result;
				if (!upgradeDb.objectStoreNames.contains(storeMeta.store)) {
					const objectStore = upgradeDb.createObjectStore(storeMeta.store, storeMeta.storeConfig);
					storeMeta.storeSchema.forEach((schema) => {
						objectStore.createIndex(schema.name, schema.keypath, schema.options);
					});
				}
			};
			upgradeRequest.onsuccess = () => {
				upgradeRequest.result.close();
				cacheDbVersion(nextVersion);
				resolve();
			};
			upgradeRequest.onerror = () =>
				reject(upgradeRequest.error || new Error(`Failed upgrading DB for store ${storeName}`));
			upgradeRequest.onblocked = async () => {
				console.warn(`[IndexedDB] Upgrade blocked for store ${storeName}, clearing DB and rebuilding.`);
				try {
					upgradeRequest.result?.close();
					await deleteDb(DBConfig.name);
					hasInitializedDb = false;
					initializeWithConfig();
					resolve();
				} catch (e) {
					reject(e);
				}
			};
		});

		return true;
	} catch (error) {
		console.error(`[IndexedDB] Failed to create object store: ${storeName}`, error);
		return false;
	} finally {
		db?.close();
	}
};

const ensureIndexedDbInitialized = (): boolean => {
	if (hasInitializedDb) {
		return true;
	}

	return initializeWithConfig();
};

export const isIndexedDbStoreMissingError = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : String(error ?? "");
	return message.includes(MISSING_STORE_MESSAGE) || message.includes(NOT_FOUND_MESSAGE);
};

export const recoverIndexedDbIfMissingStoreError = async (
	error: unknown,
	storeName?: string,
): Promise<boolean> => {
	if (!isIndexedDbStoreMissingError(error)) {
		return false;
	}

	try {
		const didInit = ensureIndexedDbInitialized();
		if (!didInit) {
			return false;
		}
		const missingStoreName = getMissingStoreNameFromError(error) || storeName;
		if (missingStoreName) {
			const created = await createObjectStoreByUpgrade(missingStoreName);
			if (!created) {
				return false;
			}
		}
		// Give browser one event loop turn to complete potential upgrade transaction.
		await new Promise((resolve) => setTimeout(resolve, 100));
		if (showSnackbar) {
			showSnackbar("數據處理完成");
		}
		return true;
	} catch (error) {
		console.error("[IndexedDB] Failed to initialize DB during recovery.", error);
		return false;
	}
};

export const useIndexedDbIfMissingStore = (storeName: string) => {
	ensureIndexedDbInitialized();
	const db = useIndexedDB(storeName);
	const hasRetriedRef = useRef(false);

	const withRecovery = useCallback(
		async <T>(operation: () => Promise<T>): Promise<T> => {
			try {
				return await operation();
			} catch (error) {
				if (hasRetriedRef.current) {
					throw error;
				}
				const didRecover = await recoverIndexedDbIfMissingStoreError(error, storeName);
				if (!didRecover) {
					throw error;
				}
				hasRetriedRef.current = true;
				
				return operation();
			}
		},
		[storeName],
	);

	return useMemo(
		() => ({
			add: <T = any>(value: T, key?: any) => withRecovery(() => db.add(value, key)),
			getByID: <T = any>(id: number | string) => withRecovery(() => db.getByID<T>(id)),
			getAll: <T = any>() => withRecovery(() => db.getAll<T>()),
			update: <T = any>(value: T, key?: any) => withRecovery(() => db.update(value, key)),
			deleteRecord: (key: any) => withRecovery(() => db.deleteRecord(key)),
			openCursor: (cursorCallback: (event: Event) => void, keyRange?: IDBKeyRange) =>
				withRecovery(() => db.openCursor(cursorCallback, keyRange)),
			getByIndex: (indexName: string, key: any) => withRecovery(() => db.getByIndex(indexName, key)),
			clear: () => withRecovery(() => db.clear()),
		}),
		[db, withRecovery],
	);
};

// Keep old name for one-to-one replacement with existing call sites.
export const initIndexedDbIfMissingStore = useIndexedDbIfMissingStore;
